import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardActions, 
  CardContent, 
  Container, 
  Grid, 
  Typography, 
  Stack,
  Chip,
  Divider,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import { 
  CheckCircle, 
  LocalOffer, 
  Storefront,
  Star,
  WorkspacePremium,
  Settings,
  ManageAccounts
} from '@mui/icons-material';
import { useApi } from '../api';
import PageBreadcrumbs from '../components/Breadcrumbs';
import { useCurrentUser } from '../auth';
import { 
  BILLING_PURCHASE_PACK_PERMISSION, 
  BILLING_PURCHASE_SUBSCRIPTION_PERMISSION,
  BILLING_MANAGE_PERMISSION 
} from '../constants/permissions';
import { useTranslation } from 'react-i18next';

type SubscriptionItem = {
  id?: string;
  name: string;
  limits: Record<string, number | null>;
  price_unit_amount?: number | null;
  price_currency?: string | null;
  active?: boolean;
  type?: 'custom' | 'regular' | 'trial';
};

type ActionPackItem = {
  id: string;
  name: string;
  action_code: string;
  units: number;
  price_unit_amount: number | null;
  price_currency: string | null;
};

const formatPrice = (amount?: number | null, currency?: string | null) => {
  if (amount == null || currency == null) return '—';
  return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
};

const BillingStore: React.FC = () => {
  const api = useApi();
  const theme = useTheme();
  const { permissions } = useCurrentUser();
  const { t } = useTranslation();
  const canBuySubscription = useMemo(() => permissions.includes(BILLING_PURCHASE_SUBSCRIPTION_PERMISSION), [permissions]);
  const canBuyPack = useMemo(() => permissions.includes(BILLING_PURCHASE_PACK_PERMISSION), [permissions]);
  const canManageBilling = useMemo(() => permissions.includes(BILLING_MANAGE_PERMISSION), [permissions]);

  const [subs, setSubs] = useState<SubscriptionItem[]>([]);
  const [packs, setPacks] = useState<ActionPackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get('/billing/subscriptions'),
          api.get('/billing/action-packs'),
        ]);
        if (!mounted) return;
        setSubs(Array.isArray(s) ? s : []);
        setPacks(Array.isArray(p) ? p : []);
      } catch (e) {
        console.error('Failed to load billing data', e);
        if (!mounted) return;
        setSubs([]);
        setPacks([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [api]);

  const sortedSubs = useMemo(() => {
    // Sort subscriptions: trial first, then regular, then custom
    return [...subs].sort((a, b) => {
      const typeOrder = { trial: 0, regular: 1, custom: 2 };
      const aType = a.type || 'regular';
      const bType = b.type || 'regular';
      return typeOrder[aType] - typeOrder[bType];
    });
  }, [subs]);

  const groupedPacks = useMemo(() => {
    const groups: Record<string, ActionPackItem[]> = {};
    for (const p of packs) {
      const key = p.action_code || 'UNKNOWN';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [packs]);

  const renderLimits = (limits: Record<string, number | null>) => {
    const entries = Object.entries(limits || {});
    if (!entries.length) return null;
    return (
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {entries.map(([code, val]) => {
          const unlimited = val == null || (typeof val === 'number' && val < 0);
          const label = t(`resources.${code}`, code);
          return (
            <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
              <Typography variant="caption" color="text.primary">
                {label}: <strong>{unlimited ? t('billing.store.unlimited', '∞') : String(val)}</strong>
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );
  };

  const onBuySubscription = async (id: string | undefined) => {
    if (!id) return;
    try {
      const res = await api.post('/billing/choose-subscription', { id });
      if (res?.checkout_url) window.location.href = res.checkout_url;
    } catch (e) {
      console.error('Failed to start subscription checkout', e);
      alert(t('billing.store.checkoutFailed'));
    }
  };

  const onBuyPack = async (id: string) => {
    try {
      const res = await api.post('/billing/buy-pack', { id });
      if (res?.checkout_url) window.location.href = res.checkout_url;
    } catch (e) {
      console.error('Failed to start pack checkout', e);
      alert(t('billing.store.checkoutFailed'));
    }
  };

  const onManageBilling = async () => {
    if (loadingPortal) return;
    setLoadingPortal(true);
    try {
      const res = await api.post('/billing/customer-portal', {});
      if (res?.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch (e) {
      console.error('Failed to access customer portal', e);
      alert(t('billing.store.portalFailed', 'Failed to access billing portal'));
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        <PageBreadcrumbs
          items={[
            { label: t('nav.dashboard'), path: '/dashboard' },
            { label: t('nav.billingStore') },
          ]}
        />
        {/* Header Section */}
        <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ fontWeight: 700, mb: 0.5 }}
          >
            {t('billing.store.title', 'Simple plans. Scalable labeling.')}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ fontWeight: 400, maxWidth: '600px', mx: 'auto' }}
          >
            {t('billing.store.subtitle', 'Start for free, grow without limits. Pay only for the labels you need.')}
          </Typography>
          
          {/* Manage Billing Button */}
          {canManageBilling && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ManageAccounts />}
                onClick={onManageBilling}
                disabled={loadingPortal}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                {loadingPortal 
                  ? t('billing.store.loading', 'Loading...') 
                  : t('billing.store.manageBilling', 'Manage Billing')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Subscriptions Section */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WorkspacePremium color="primary" fontSize="small" />
            {t('billing.store.subscriptions', 'Subscription Plans')}
          </Typography>
          
          <Grid container spacing={2}>
            {sortedSubs.map((it, idx) => {
              const subType = it.type || 'regular';
              const isTrial = subType === 'trial';
              const isCustom = subType === 'custom';
              const isRegular = subType === 'regular';
              const isProfessional = it.name?.toLowerCase().includes('professional');
              const isActive = it.active === true;
              const hasPrice = it.price_unit_amount != null && it.price_unit_amount > 0;
              
              // Calculate grid size based on total number of subscriptions
              const totalSubs = sortedSubs.length;
              const gridSize = totalSubs === 1 ? 12 : totalSubs === 2 ? 6 : 4;
              
              return (
                <Grid item xs={12} md={gridSize} key={`sub-${idx}-${it.id || 'trial'}`}>
                  <Card 
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: `2px solid ${isActive ? theme.palette.success.main : (isCustom ? theme.palette.warning.main : (isProfessional ? theme.palette.primary.main : theme.palette.divider))}`,
                      borderRadius: 2,
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      backgroundColor: isActive 
                        ? alpha(theme.palette.success.main, 0.02) 
                        : isCustom 
                          ? alpha(theme.palette.warning.main, 0.02)
                          : 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8],
                      },
                    }}
                  >
                    {isActive ? (
                      <Chip 
                        label={t('billing.store.currentPlan', 'Current Plan')}
                        color="success"
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    ) : isCustom ? (
                      <Chip 
                        label={t('billing.store.customPlan', 'Custom Plan')}
                        color="warning"
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    ) : isProfessional ? (
                      <Chip 
                        label={t('billing.store.bestValue', 'Best Value')}
                        color="primary"
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    ) : null}
                    
                    <CardContent sx={{ p: 2.5, pb: 1 }}>
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        {it.name || 'Plan'}
                      </Typography>
                      
                      {isCustom ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                            {t('billing.store.customDesc', 'A solution specially tailored for your company in collaboration with the app owner.')}
                          </Typography>
                          {hasPrice && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography 
                                variant="h4" 
                                component="div" 
                                sx={{ fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 0.5 }}
                              >
                                {it.price_currency?.toUpperCase()} {(it.price_unit_amount! / 100).toFixed(2)}
                                <Typography variant="body2" color="text.secondary">
                                  /{t('billing.store.month', 'month')}
                                </Typography>
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography 
                              variant="h4" 
                              component="div" 
                              sx={{ fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 0.5 }}
                            >
                              {isTrial ? (
                                <span style={{ color: theme.palette.success.main }}>
                                  {t('billing.store.free', 'FREE')}
                                </span>
                              ) : (
                                <>
                                  {it.price_currency?.toUpperCase()} {(it.price_unit_amount! / 100).toFixed(2)}
                                  <Typography variant="body2" color="text.secondary">
                                    /{t('billing.store.month', 'month')}
                                  </Typography>
                                </>
                              )}
                            </Typography>
                          </Box>

                          {isTrial && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              {t('billing.store.starterDesc', 'Perfect for small sellers or testing new markets')}
                            </Typography>
                          )}
                          
                          {isProfessional && isRegular && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              {t('billing.store.professionalDesc', 'Unlimited products, full automation, and premium support.')}
                            </Typography>
                          )}
                        </>
                      )}

                      <Divider sx={{ my: 1 }} />
                      
                      {renderLimits(it.limits)}
                    </CardContent>
                    
                    <CardActions sx={{ p: 2.5, pt: 0 }}>
                      {isActive ? (
                        <Typography 
                          variant="caption" 
                          color="success.main" 
                          align="center" 
                          sx={{ width: '100%', py: 1, fontWeight: 600 }}
                        >
                          {t('billing.store.activeSubscription', 'Active subscription')}
                        </Typography>
                      ) : it.id && canBuySubscription ? (
                        <Button
                          variant={isProfessional || isCustom ? 'contained' : 'outlined'}
                          color={isCustom ? 'warning' : 'primary'}
                          size="medium"
                          fullWidth
                          onClick={() => onBuySubscription(it.id)}
                          sx={{ 
                            py: 1, 
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {isTrial 
                            ? t('billing.store.createFreeAccount', 'Create Free Account') 
                            : t('billing.store.buyNow', 'Buy Now')}
                        </Button>
                      ) : !isActive && isTrial ? (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          align="center" 
                          sx={{ width: '100%', py: 0.5 }}
                        >
                          {t('billing.store.noCardRequired', 'No credit card required · Instant setup')}
                        </Typography>
                      ) : null}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Action Packs Section */}
        {groupedPacks.length > 0 && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h6" 
                component="h2" 
                gutterBottom 
                sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}
              >
                <LocalOffer color="primary" fontSize="small" />
                {t('billing.store.needMoreTranslations', 'Need more label translations?')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('billing.store.packagesDesc', 'Buy additional packs anytime. Available in Starter and Professional accounts')}
              </Typography>
            </Box>

            {groupedPacks.map(([actionCode, items]) => (
              <Box key={actionCode} sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  {items.map((it) => {
                    const unitsPerPrice = it.price_unit_amount 
                      ? (it.price_unit_amount / 100 / it.units).toFixed(2)
                      : null;
                    
                    return (
                      <Grid item xs={12} sm={6} md={3} key={`pack-${it.id}`}>
                        <Card 
                          elevation={0}
                          sx={{
                            height: '100%',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              borderColor: theme.palette.primary.main,
                              boxShadow: theme.shadows[6],
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2, pb: 1 }}>
                            <Typography 
                              variant="subtitle1" 
                              component="h3" 
                              sx={{ fontWeight: 700, mb: 1 }}
                            >
                              {t('billing.store.package', 'Package')} {it.units}
                            </Typography>
                            
                            <Box sx={{ mb: 1 }}>
                              <Typography 
                                variant="h5" 
                                component="div" 
                                sx={{ fontWeight: 700 }}
                              >
                                {it.price_currency?.toUpperCase()} {(it.price_unit_amount! / 100).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                /{t('billing.store.package', 'package')}
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
                              <Typography variant="caption">
                                {it.units} {t('billing.store.perLanguageTranslation', 'per language translation')}
                              </Typography>
                            </Box>

                            {unitsPerPrice && (
                              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
                                <Typography variant="caption">
                                  {unitsPerPrice} {it.price_currency?.toUpperCase()} {t('billing.store.perLanguageTranslationUnit', 'per language translation')}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
                              <Typography variant="caption">
                                {t('billing.store.noTimeLimit', 'No usage time limit')}
                              </Typography>
                            </Box>
                          </CardContent>
                          
                          {canBuyPack && (
                            <CardActions sx={{ p: 2, pt: 0 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => onBuyPack(it.id)}
                                sx={{ 
                                  py: 0.75, 
                                  fontWeight: 600,
                                  fontSize: '0.8rem'
                                }}
                              >
                                {t('billing.store.buyNow', 'Buy Now')}
                              </Button>
                            </CardActions>
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default BillingStore;
